import * as d3 from 'd3'
import * as dateFns from 'date-fns'

import { Instrument, ObjectHash } from '../../types/shared'
import { DateRangeType, ScaleType, SpecificChartFunctionality } from '../constants/common'
import Chart from '../models/chart'
import { ISettings } from '../models/chart_settings/interfaces'
import Pane, { ScaleAxis } from '../models/pane'
import Quote from '../models/quote'
import Utils, { convertLocalToNyTime, dateFromUnixTimestamp } from '../utils'
import { getBarWidthWithMargin, getZoomFactorForBarsToDisplay } from '../utils/chart'
import { DATE_RANGE_PREFIX_DELMITER, parseCustomDateRangeUrlParam } from '../utils/chart-date-range-utils'

interface VisibleBarNumberProps {
  start: Date | null
  end: Date
  quote: Quote
  dateRange: string
}

interface ChartZoomAndLeftOffsetProps extends Omit<VisibleBarNumberProps, 'dateRange'> {
  chartVisibleWidth: number
  numOfBarsToRender?: number
  chartModel: Chart
}

interface ChartStartEndDatesProps {
  dateRange: string
  quote: Quote
}

export const getVisibleBarsNumber = ({ start, end, quote, dateRange }: VisibleBarNumberProps) => {
  const startInSec = start ? start.getTime() / 1000 : 0
  const endInSec = end.getTime() / 1000
  const isQuoteDateInRange = (quoteDate: number) => quoteDate >= startInSec && quoteDate <= endInSec
  const lastQuoteDateIndex = quote.date.length - 1
  let lastVisibleBarIndex = lastQuoteDateIndex
  let numOfVisibleBars = 0
  if ([DateRangeType.d1, DateRangeType.d5].includes(dateRange as DateRangeType)) {
    numOfVisibleBars = quote.drawMinutesPerDay
  } else {
    let firstVisibleBarIndex = -1
    lastVisibleBarIndex = -1
    quote.date.forEach((quoteDate, index) => {
      if (isQuoteDateInRange(quoteDate)) {
        if (firstVisibleBarIndex === -1) {
          firstVisibleBarIndex = index
        }
        lastVisibleBarIndex = index
      }
    })
    numOfVisibleBars = lastVisibleBarIndex - firstVisibleBarIndex + 1

    if (firstVisibleBarIndex === -1 && lastVisibleBarIndex === -1) {
      return { numOfVisibleBars: 0, numOfBarsOffset: 0 }
    }
  }

  return { numOfVisibleBars, numOfBarsOffset: lastQuoteDateIndex - lastVisibleBarIndex }
}

export const getChartZoomAndLeftOffset = ({
  chartVisibleWidth,
  start,
  end,
  quote,
  chartModel,
  numOfBarsToRender,
}: ChartZoomAndLeftOffsetProps) => {
  const { numOfVisibleBars, numOfBarsOffset } = getVisibleBarsNumber({
    start,
    end,
    quote,
    dateRange: chartModel.dateRange,
  })
  if (numOfVisibleBars === 0) {
    return { zoomFactor: chartModel.zoomFactor, leftOffset: chartModel.leftOffset }
  }

  const chartLayout = chartModel.chart_layout()
  const zoomFactor = getZoomFactorForBarsToDisplay({
    chartLayout,
    chartVisibleWidth,
    numOfVisibleBars: numOfBarsToRender ?? numOfVisibleBars,
  })
  const barWidth = getBarWidthWithMargin({ chartLayout, zoomFactor })
  const leftOffset =
    getLeftOffset({
      quote,
      chartModel,
      config: {},
      barWidth,
      numberOfBarsToDraw: numOfBarsToRender,
    }) +
    numOfBarsOffset * barWidth
  return { zoomFactor, leftOffset }
}

export const getChartStartEndDates = ({ dateRange, quote }: ChartStartEndDatesProps) => {
  const lastDate = quote.date[quote.date.length - 1]
  const dates = quote.getDaysInQuote()

  const end = dateFromUnixTimestamp(lastDate)

  if (dateRange.includes(DATE_RANGE_PREFIX_DELMITER)) {
    const range = parseCustomDateRangeUrlParam({ dateRange, anchorDate: end })
    return {
      start: range.start && convertLocalToNyTime(range.start, false),
      end: convertLocalToNyTime(range.end ?? end, false),
    }
  }

  let start
  // No condition is needed here, but this would be dateRangePrefix === DateRangePrefixEnum.Enum
  switch (dateRange) {
    case DateRangeType.d1:
      end.setHours(23, 59, 59, 999)
      start = dates.length === 0 ? end : dates[dates.length - 1]
      break
    case DateRangeType.d5:
      end.setHours(23, 59, 59, 999)
      const startDateIndex = dates.length === 0 ? -1 : Math.max(dates.length - 5, 0)
      start = startDateIndex === -1 ? end : dates[startDateIndex]
      break
    case DateRangeType.m1:
      start = dateFns.subMonths(end, 1)
      break
    case DateRangeType.m3:
      start = dateFns.subMonths(end, 3)
      break
    case DateRangeType.m6:
      start = dateFns.subMonths(end, 6)
      break
    case DateRangeType.ytd:
      start = dateFns.startOfYear(end)
      break
    case DateRangeType.y1:
      start = dateFns.subYears(end, 1)
      break
    case DateRangeType.y2:
      start = dateFns.subYears(end, 2)
      break
    case DateRangeType.y5:
      start = dateFns.subYears(end, 5)
      break
    case DateRangeType.max:
    default:
      start = null
      break
  }

  return { start: start && convertLocalToNyTime(start, false), end: convertLocalToNyTime(end, false) }
}

interface GetLeftOffsetProps {
  quote: Quote
  chartModel: Chart
  config?: { stretch?: boolean }
  barWidth?: number
  barIndex?: number
  numberOfBarsToDraw?: number
  shouldAlignDaysToLeftApplied?: boolean
}

export const getMinMaxLeftOffset = ({
  newLeftOffset,
  chartModel,
  barWidth = getBarWidthWithMargin({ zoomFactor: chartModel.zoomFactor, chartLayout: chartModel.chart_layout() }),
  barIndex,
}: {
  newLeftOffset: number
  chartModel: Chart
  barWidth?: number
  barIndex: number
}) => {
  const { ChartSettings } = chartModel.getChartLayoutSettings()
  const width = chartModel.width - ChartSettings.left.width - ChartSettings.right.width
  const numOfBarsInView = Math.floor(width / barWidth)
  const fx = (index: number) => index * barWidth
  return ~~Utils.min(barWidth / 2, Utils.max(newLeftOffset, width - fx(barIndex + numOfBarsInView)))
}

export const getLeftOffset = ({
  quote,
  chartModel,
  config,
  barWidth = getBarWidthWithMargin({ zoomFactor: chartModel.zoomFactor, chartLayout: chartModel.chart_layout() }),
  barIndex = getBarIndex(quote),
  numberOfBarsToDraw,
  shouldAlignDaysToLeftApplied = true,
}: GetLeftOffsetProps) => {
  const { ChartSettings } = chartModel.getChartLayoutSettings()
  const width = chartModel.width - ChartSettings.left.width - ChartSettings.right.width
  const specificChartFunctionality = chartModel.chart_layout().specificChartFunctionality
  if (config?.stretch) {
    return 0
  }
  if (getIsSmallIndexChart(specificChartFunctionality)) {
    return barWidth
  }
  const fx = (index: number) => index * barWidth
  let leftOffset = width - fx(barIndex) - Math.round(barWidth / 2)
  leftOffset = Math.round(Utils.min(barWidth / 2, Utils.max(leftOffset, width - fx(barIndex + 45))))
  if (
    shouldAlignDaysToLeftApplied &&
    shouldAlignDaysToLeft({
      quote,
      numberOfBarsToDraw,
      specificChartFunctionality,
      dateRange: chartModel.dateRange,
    })
  ) {
    let drawDaysInOneChart = 1
    if (numberOfBarsToDraw) {
      drawDaysInOneChart = numberOfBarsToDraw / quote.getBarsInDay()!
    } else if ([10, 15].includes(quote.interval) || chartModel.dateRange === DateRangeType.d5) {
      drawDaysInOneChart = 5
    } else if (quote.interval >= 30) {
      drawDaysInOneChart = (quote.interval / 30) * 10
    }

    const drawDaysWidth = drawDaysInOneChart * quote.getBarsInDay()! * barWidth

    // +1 is a buffer as it often happens drawDaysWidth is wider than width by tiny amount (< 1/10000 of px)
    if (drawDaysWidth <= width + 1) {
      // Align start of day to left if we have enough space to show all bars in day
      const barsToOffset = (Object.keys(quote.getDayToOffset()).length - drawDaysInOneChart) * quote.getBarsInDay()!
      leftOffset = -fx(barsToOffset) + ~~(barWidth / 2)
    }
  }
  // getMinMaxLeftOffset move left offset to keep at least one bar in view what isn't desired for offscreen charts
  if (specificChartFunctionality === SpecificChartFunctionality.offScreen) {
    return leftOffset
  }
  return getMinMaxLeftOffset({
    newLeftOffset: leftOffset,
    chartModel,
    barWidth,
    barIndex,
  })
}

export const getBarIndex = (quote: Quote) => quote?.getBarIndex(quote.open?.length ? quote.open.length - 1 : 0) || 0

export const getIsSmallIndexChart = (specificChartFunctionality: SpecificChartFunctionality) =>
  specificChartFunctionality === SpecificChartFunctionality.smallIndex

interface ShouldAlignDaysToLeftProps {
  quote: Quote
  numberOfBarsToDraw?: number
  specificChartFunctionality: SpecificChartFunctionality
  dateRange: DateRangeType | null
}

/**
 * Evaluate if days should be aligned to the left
 * @param {object} object.quote - Quote model
 * @param {string} object.specificChartFunctionality - SpecificChartFunctionality enum
 * @returns {boolean} boolean
 * */
export const shouldAlignDaysToLeft = ({
  quote,
  dateRange,
  numberOfBarsToDraw,
  specificChartFunctionality,
}: ShouldAlignDaysToLeftProps) => {
  const isStockIntraday = quote.isIntraday && quote.instrument === Instrument.Stock
  // We don't want this to affect /charts page, only if intraday daterange is applied
  const isQuoteOrOffscreen = [SpecificChartFunctionality.quotePage, SpecificChartFunctionality.offScreen].includes(
    specificChartFunctionality
  )
  const isChartPage = specificChartFunctionality === SpecificChartFunctionality.chartPage
  const isChartsInradayDateRange =
    isChartPage && !!dateRange && [DateRangeType.d1, DateRangeType.d5].includes(dateRange)
  const isChartPageWithFixedBars = isChartPage && numberOfBarsToDraw
  return isStockIntraday && (isQuoteOrOffscreen || isChartsInradayDateRange || isChartPageWithFixedBars)
}

interface ClipProps {
  canvasCtx: RenderingContext2DType
  width: number
  height: number
  Settings: ISettings['ChartSettings']
}
// pane canvasCtx clip
export const clip = ({ canvasCtx, width, height, Settings }: ClipProps) => {
  const contentWidth = width - Settings.left.width - Settings.right.width
  const contentHeight = height - Settings.top.height - Settings.bottom.height
  canvasCtx.beginPath()
  canvasCtx.rect(Settings.left.width, Settings.top.height, contentWidth, contentHeight)
  canvasCtx.clip()
}

export const getSettings = (paneModel: Pane) => {
  const { ChartSettings, IndicatorSettings } = paneModel.chart().getChartLayoutSettings()
  for (const element of paneModel.elements().all()) {
    if (element.isIndicator()) {
      return IndicatorSettings
    }
  }
  return ChartSettings
}

interface RecountScaleProps {
  chartModel: Chart
  paneModel: Pane
}

type ScaledFn = Partial<ScaleAxis> & (d3.ScaleLinear<number, number> | d3.ScaleSymLog<number, number>)

export const recountScale = ({ chartModel, paneModel }: RecountScaleProps) => {
  const { ChartSettings, IndicatorSettings, MarketSentimentSettings } = chartModel.getChartLayoutSettings()
  const chartLayout = chartModel.chart_layout()
  const barWidthWithMargin = getBarWidthWithMargin({ chartLayout, zoomFactor: chartModel.zoomFactor })
  for (const elementModel of paneModel.elements().all()) {
    const el = elementModel.instance
    const isChart = elementModel.isChart()
    if (isChart || elementModel.isIndicator()) {
      let fx: ScaledFn, Settings: ISettings['MarketSentimentSettings' | 'ChartSettings' | 'IndicatorSettings'], width
      const scaleType = isChart ? chartModel.scale : ScaleType.Linear
      if (isChart) {
        Settings = chartModel.instrument === Instrument.MarketSentiment ? MarketSentimentSettings : ChartSettings
      } else {
        Settings = IndicatorSettings as ISettings['IndicatorSettings']
      }
      const contentHeight = paneModel.height - Settings.top.height - Settings.bottom.height

      if (chartModel.stretch) {
        width = chartModel.width - Settings.left.width - Settings.right.width
        fx = d3
          .scaleLinear()
          .range([0, width])
          .domain([0, Math.max(0, chartModel.quote().close.length - 1)]) as ScaledFn
      } else {
        fx = ((index: number) => index * barWidthWithMargin) as ScaledFn
        fx.invert = (x: number) => x / barWidthWithMargin
        fx.width = function (w) {
          return this.invert(w) - this.invert(0)
        }
      }
      paneModel.updateAttribute('scale', { x: fx }) // some elements need fx to count getMinMax

      const { min, max } = paneModel.scaleRange || el.getMinMax!()
      let fy

      switch (scaleType) {
        case ScaleType.Logarithmic:
          fy = d3.scaleSymlog().range([0, contentHeight]).domain([max, min]) as ScaledFn
          break
        default:
          fy = d3.scaleLinear().range([0, contentHeight]).domain([max, min]) as ScaledFn
          if (!paneModel.scaleRange) {
            fy = fy.nice(10)
          }

          break
      }
      fy.height = function (h: number) {
        return this.invert(h) - this.invert(0)
      }
      paneModel.updateAttribute('scale', {
        x: fx,
        y: fy,
      })

      if (isChart) {
        el.setupAxis!(fx as ScaleAxis)
      }
      break
    }
  }
}

// general, only when blurry ends won't be visible or didn't matter
export const getOffsetFromLineWidth = (lineWidth: number) => (lineWidth % 2 === 0 ? 0 : 0.5)

interface IXYOffsetFromLine {
  lineWidth: number
  x1: number
  y1: number
  x2: number
  y2: number
}

// return x,y offsets for pixel perfect line rendering
export const getXYOffsetFromLine = ({ lineWidth, x1, y1, x2, y2 }: IXYOffsetFromLine) => {
  // don't add offset is lineWidth is even
  if (lineWidth % 2 === 0) {
    return { x: 0, y: 0 }
  }
  if (y1 === y2) {
    return { x: 0, y: 0.5 } // horizontal line
  } else if (x1 === x2) {
    return { x: 0.5, y: 0 } // vertical line
  } else {
    return { x: 0.5, y: 0.5 } // diagonal line
  }
}

export const getRoundedObject = <T extends ObjectHash>(obj: T): T => {
  const rounded = Object.entries<number>(obj)
  for (let i = 0; i < rounded.length; i++) {
    rounded[i][1] = Math.round(rounded[i][1])
  }
  return Object.fromEntries(rounded) as T
}

interface ITranslate {
  context: CanvasRenderingContext2D
  xOffset: number
  yOffset: number
}

interface ITranslateObj {
  do: () => void
  undo: () => void
}

export const getTranslate = ({ context, xOffset, yOffset }: ITranslate): ITranslateObj => ({
  do: () => context.translate(xOffset, yOffset),
  undo: () => context.translate(xOffset * -1, yOffset * -1),
})

interface IPercentageScaleValue {
  number: number
  base: number
}

export const getPercentageFromValue = ({ number, base }: IPercentageScaleValue) => ((number - base) / base) * 100

export const getValueFromPercentage = ({ number, base }: IPercentageScaleValue) => (number / 100) * base + base
